import {
  isProjectSelectable,
  type LiveProject,
} from '@wiztime/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { settingsPath, SettingsSection } from '../../../app/routes';
import type { ProjectsView } from '../../../hooks/useProjects';
import { cx } from '../../../lib/cx';
import { AddPopover } from '../../../ui/AddPopover';
import { Card } from '../../../ui/Card';
import { CollapseAllIcon, ExpandAllIcon } from '../../../ui/icons';
import { readCollapsedParents, writeCollapsedParents } from '../collapsedParents';
import { useDayUi } from '../dayUiStore';
import { AddProjectForm, type NewProjectInput } from '../project/AddProjectForm';
import { QuickMarkMode } from '../quickMark';
import { childCountOf, topLevelSelectable, toggled, type ProjectGroup } from '../projectGroups';
import { useProjectGroups } from '../useProjectGroups';
import { QuickMarkTile, useTileDragContext } from './QuickMarkTile';
import { TileUiHost, TileUiKind, type TileUi } from './QuickMarkTileUi';
import { useCreateQuickProject } from './useCreateQuickProject';
import styles from './Cards.module.css';

/**
 * The two faces of the quick-mark card: the day view's column, and the week view's right sidebar
 * (settings-revisions-and-layout.md §3.4), whose tiles are compact. Same state store, same
 * selection styles.
 */
export const QuickMarkLayout = { Column: 'column', Sidebar: 'sidebar' } as const;
export type QuickMarkLayout = (typeof QuickMarkLayout)[keyof typeof QuickMarkLayout];

/** The "+" button and its form in the card's title row. */
function AddProjectPopover({
  creator,
  parents,
  open,
  presetParentId,
  onOpenChange,
  onCreated,
}: {
  creator: ReturnType<typeof useCreateQuickProject>;
  /** Active top-level projects offered as parent, in settings order. */
  parents: readonly LiveProject[];
  open: boolean;
  /** The parent preselected by the tile menu's "add child project"; null when opened with the button. */
  presetParentId: string | null;
  onOpenChange: (open: boolean) => void;
  onCreated: (input: NewProjectInput) => void;
}) {
  const { t } = useTranslation();
  return (
    <AddPopover label={t('newProject.add')} testId="quick-mark-add" open={open} onOpenChange={onOpenChange}>
      {(close) => (
        <AddProjectForm
          key={presetParentId ?? 'none'}
          existingNames={creator.existingNames}
          parents={parents}
          initialParentId={presetParentId}
          onSubmit={async (input) => {
            const failure = await creator.create(input);
            if (failure === null) onCreated(input);
            return failure;
          }}
          onDone={close}
        />
      )}
    </AddPopover>
  );
}

/**
 * Project tiles of the quick-mark brush (2026-10b §4.5 variant A, §4.8): fixed settings order
 * with an ordinal on every tile (the digit keys address 1–9). The projects arrive by property — the day and week views each pass their
 * own list (§4.6) — and `all` keeps the settings order that the ranked `active` list of the
 * pickers does not. `layout` picks the day view's column (the default) or the week view's
 * sidebar with compact tiles; both share the state store and the selection styles.
 *
 * Parents with children start expanded; the collapsed ones are remembered on this device
 * (design 2026-10-15 §1). Children are added through the title row's "+" popover (its parent
 * select; the tile menu preselects the parent), and any tile can be renamed in place (§2).
 */
export function QuickMarkCard({
  projects,
  layout = QuickMarkLayout.Column,
}: {
  projects: ProjectsView;
  layout?: QuickMarkLayout;
}) {
  const { t } = useTranslation();
  const quickMark = useDayUi((state) => state.quickMark);
  const creator = useCreateQuickProject(projects);
  const drag = useTileDragContext();
  const byId = projects.byId;
  const tiles = topLevelSelectable(projects.all, byId);
  const selectable = useMemo(
    () => projects.all.filter((project) => isProjectSelectable(project, byId)),
    [projects.all, byId],
  );
  const groups = useProjectGroups(selectable, projects);
  const groupOf = new Map<string, ProjectGroup>(groups.map((group) => [group.project.id, group]));
  const [collapsed, setCollapsedState] = useState<ReadonlySet<string>>(readCollapsedParents);
  const [earlierOpen, setEarlierOpen] = useState<ReadonlySet<string>>(new Set());
  const [tileUi, setTileUi] = useState<TileUi | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [presetParentId, setPresetParentId] = useState<string | null>(null);
  const compact = layout === QuickMarkLayout.Sidebar;

  const setCollapsed = (next: ReadonlySet<string>): void => {
    setCollapsedState(next);
    writeCollapsedParents(next);
  };
  const withChildren = tiles.filter((project) => {
    const group = groupOf.get(project.id);
    return group !== undefined && childCountOf(group) > 0;
  });
  const allExpanded = withChildren.every((project) => !collapsed.has(project.id));
  const allCollapsed = withChildren.every((project) => collapsed.has(project.id));
  const expandAll = (): void => setCollapsed(new Set([...collapsed].filter((id) => !withChildren.some((p) => p.id === id))));
  const collapseAll = (): void => setCollapsed(new Set([...collapsed, ...withChildren.map((project) => project.id)]));

  const openMenu = (project: LiveProject, anchor: HTMLElement, point: { readonly x: number; readonly y: number }): void =>
    setTileUi({ kind: TileUiKind.Menu, project, anchor, ...point });
  const openRename = (project: LiveProject, anchor: HTMLElement): void =>
    setTileUi({ kind: TileUiKind.Rename, project, anchor });
  const expandParent = (parentId: string): void => {
    // A new child must be visible: an expanded parent shows it right away.
    if (!collapsed.has(parentId)) return;
    const next = new Set(collapsed);
    next.delete(parentId);
    setCollapsed(next);
  };
  // The tile menu's "add child project" opens the title row's "+" popover with the parent chosen.
  const openAddChild = (parent: LiveProject): void => {
    expandParent(parent.id);
    setPresetParentId(parent.id);
    setAddOpen(true);
  };
  const closeTileUi = (): void => setTileUi(null);

  const expandButtons =
    withChildren.length === 0 ? null : (
      <>
        <button
          type="button"
          className={styles.cardIconButton}
          aria-label={t('quickMark.expandAll')}
          title={t('quickMark.expandAll')}
          disabled={allExpanded}
          data-testid="quick-mark-expand-all"
          onClick={expandAll}
        >
          <ExpandAllIcon />
        </button>
        <button
          type="button"
          className={styles.cardIconButton}
          aria-label={t('quickMark.collapseAll')}
          title={t('quickMark.collapseAll')}
          disabled={allCollapsed}
          data-testid="quick-mark-collapse-all"
          onClick={collapseAll}
        >
          <CollapseAllIcon />
        </button>
      </>
    );

  return (
    <Card
      title={t('quickMark.title')}
      hint={compact ? undefined : t('quickMark.hint')}
      extra={
        projects.loading || projects.error !== null ? undefined : (
          <span className={styles.cardActions}>
            {expandButtons}
            <AddProjectPopover
              creator={creator}
              parents={tiles}
              open={addOpen}
              presetParentId={presetParentId}
              onOpenChange={(next) => {
                // Opened by the button itself: no parent preselected.
                if (next && !addOpen) setPresetParentId(null);
                setAddOpen(next);
              }}
              onCreated={({ parentId }) => {
                if (parentId !== null) expandParent(parentId);
              }}
            />
          </span>
        )
      }
      loading={projects.loading}
      error={projects.error !== null}
      errorText={t('timeline.loadError')}
      onRetry={projects.retry}
      testId="quick-mark-card"
    >
      {tiles.length === 0 ? (
        <p className={styles.empty}>
          {t('quickMark.empty')} ·{' '}
          <Link to={settingsPath(SettingsSection.Projects)}>{t('quickMark.addProject')}</Link>
        </p>
      ) : null}
      <ul className={cx(styles.tiles, compact && styles.tilesCompact)}>
        {tiles.map((project, index) => {
          const group = groupOf.get(project.id);
          const childCount = group === undefined ? 0 : childCountOf(group);
          const isOpen = !collapsed.has(project.id);
          const modeOf = (id: string): QuickMarkMode => (id === quickMark.projectId ? quickMark.mode : QuickMarkMode.Off);
          const childTile = (child: LiveProject) => (
            <li key={child.id}>
              <QuickMarkTile
                project={child}
                mode={modeOf(child.id)}
                projectsById={byId}
                drag={drag}
                compact={compact}
                child
                onMenu={openMenu}
                onRename={openRename}
              />
            </li>
          );
          return (
            <li key={project.id}>
              <div className={styles.tileRow}>
                <QuickMarkTile
                  project={project}
                  mode={modeOf(project.id)}
                  ordinal={index + 1}
                  projectsById={byId}
                  drag={drag}
                  compact={compact}
                  onMenu={openMenu}
                  onRename={openRename}
                />
                {childCount > 0 ? (
                  <button
                    type="button"
                    className={styles.tileExpand}
                    aria-expanded={isOpen}
                    aria-label={t(isOpen ? 'quickMark.collapseChildren' : 'quickMark.expandChildren', {
                      name: project.data.name,
                      count: childCount,
                    })}
                    title={t(isOpen ? 'quickMark.collapseChildren' : 'quickMark.expandChildren', {
                      name: project.data.name,
                      count: childCount,
                    })}
                    data-testid="quick-mark-expand"
                    onClick={() => {
                      const next = new Set(collapsed);
                      if (isOpen) next.add(project.id);
                      else next.delete(project.id);
                      setCollapsed(next);
                    }}
                  >
                    <span className={cx(styles.tileChevron, isOpen && styles.tileChevronOpen)} aria-hidden="true" />
                    <span>{childCount}</span>
                  </button>
                ) : null}
              </div>
              {group !== undefined && childCount > 0 && isOpen ? (
                <ul className={cx(styles.tiles, styles.childTiles, compact && styles.tilesCompact)}>
                  {group.fresh.map(childTile)}
                  {group.folded.length > 0 ? (
                    <li>
                      <button
                        type="button"
                        className={styles.earlierToggle}
                        aria-expanded={earlierOpen.has(project.id)}
                        data-testid="quick-mark-earlier"
                        onClick={() => setEarlierOpen((previous) => toggled(previous, project.id))}
                      >
                        {t('quickMark.earlier', { count: group.folded.length })}
                      </button>
                    </li>
                  ) : null}
                  {earlierOpen.has(project.id) ? group.folded.map(childTile) : null}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
      <TileUiHost
        tileUi={tileUi}
        projects={projects}
        creator={creator}
        onClose={closeTileUi}
        onRename={openRename}
        onAddChild={openAddChild}
      />
    </Card>
  );
}
